/**
 * Shared state for the auto-approve countdown shown on human-in-the-loop
 * interrupt cards (prompt enhancement, prompt reuse, plan approval).
 *
 * The countdown is client-side only — nothing on the server enforces it.  Its
 * one piece of persistence is the deadline, mirrored into ``localStorage`` so a
 * browser refresh resumes with the time that is actually left instead of
 * restarting from the full duration.  Storing an absolute timestamp on the same
 * machine that reads it back means there is no clock skew to correct for.
 *
 * Contract for a stored deadline:
 *  - absent        → no countdown was running; start a fresh one.
 *  - in the future → resume with the remaining seconds.
 *  - just passed   → the window was missed while away; act immediately.
 *  - long passed   → treated as absent, so returning to a thread abandoned days
 *                    ago gets a full countdown rather than an instant approve.
 */

/** Seconds the user gets to act before an interrupt card auto-approves. */
export const AUTO_APPROVE_SECONDS = 120;

const STORAGE_PREFIX = 'interrupt-deadline:';

/** A deadline this far in the past is stale rather than "just missed". */
const STALE_AFTER_MS = 60 * 60 * 1000;

function storageKey(threadId: string): string {
    return `${STORAGE_PREFIX}${threadId}`;
}

/**
 * Seconds left on a stored deadline, or ``null`` when there is nothing usable
 * to resume from.  Returns 0 when the deadline passed while the user was away.
 */
export function readCountdown(threadId: string): number | null {
    if (!threadId) return null;
    try {
        const raw = localStorage.getItem(storageKey(threadId));
        if (!raw) return null;

        const deadlineAt = Number(raw);
        if (!Number.isFinite(deadlineAt)) {
            clearCountdown(threadId);
            return null;
        }

        const msLeft = deadlineAt - Date.now();
        if (msLeft < -STALE_AFTER_MS) {
            clearCountdown(threadId);
            return null;
        }
        return Math.max(0, Math.ceil(msLeft / 1000));
    } catch {
        // Private browsing or blocked site data — fall back to no countdown.
        return null;
    }
}

/** Persist the deadline implied by `secondsRemaining` counted from right now. */
export function writeCountdown(threadId: string, secondsRemaining: number): void {
    if (!threadId) return;
    try {
        localStorage.setItem(storageKey(threadId), String(Date.now() + secondsRemaining * 1000));
    } catch {
        // Nothing to do — the countdown still runs, it just won't survive a reload.
    }
}

/** Drop the stored deadline once the interrupt has been resolved. */
export function clearCountdown(threadId: string): void {
    if (!threadId) return;
    try {
        localStorage.removeItem(storageKey(threadId));
    } catch {
        // Ignore — a stale key self-expires via STALE_AFTER_MS.
    }
}

/** Format seconds as M:SS for the countdown badge. */
export function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}
